import React from "react";
import { formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown, X, Search as SearchIcon, Layers, CheckCircle2, Undo2, FileX,
} from "lucide-react";
import GiftCardActions from "./GiftCardActions";
import { fmtDate, fmtDateTime } from "./dateFmt";
import { clientSeriesValidity } from "@/lib/seriesValidity";

// Iter-f10 — Resolve the Bali-local (or branch-local) calendar date of
// a payment timestamp so the X-vs-Refund gate can compare it to the
// branch's current business day without pulling anything from the
// locked ReportingEngine. Returns `null` when the input is unparseable.
function _dateInTz(iso, tz) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: tz || "Asia/Makassar" });
  } catch {
    return null;
  }
}
function _todayInTz(tz) {
  try {
    return new Date().toLocaleDateString("en-CA", { timeZone: tz || "Asia/Makassar" });
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/* ---------------- PaymentSection ----------------
 *  The lower half of OrderDetail — closed banner, "ready-to-close"
 *  banner, series slide-down, per-category payment tabs, recorded
 *  payments list, refunds-issued list.
 *
 *  A "dumb" card that takes every piece of state + every handler as
 *  props so the parent OrderDetail still owns the mutating logic and
 *  the `refresh()` cycle after each server round-trip.
 * ------------------------------------------------------------------ */
export default function PaymentSection({
  order, branch,
  isClosed, total, due,
  paymentTabsMeta, onlineProviders, onlineProviderLabel,
  closeOrder,
  seriesOptions, seriesPanelOpen, setSeriesPanelOpen, payWithSeries,
  activeCategories,
  paymentTab, setPaymentTab,
  paymentTypeId, setPaymentTypeId, typesForTab,
  onlineProvider, setOnlineProvider,
  isVoucherType,
  voucherCode, setVoucherCode,
  voucherInfo, setVoucherInfo, voucherChecking, searchVoucher,
  paymentAmount, setPaymentAmount,
  addPayment, removePayment,
  // Fix 1 (Iter-f10/f11): the X delete-icon on a Recorded Payments row
  // is removed from past payments; past rows swap to a Refund button
  // (role-gated on the parent). Parent owns the refund side-effect
  // (confirmation dialog + API round-trip) and passes a handler
  // through here. Owner / Admin only.
  onRefundPayment,
  // Iter-f11 — Pending deposit payments show a Write Off action next
  // to Refund (Owner / Admin only). Parent opens a reason dialog
  // before hitting the write-off endpoint.
  onWriteOffDeposit,
  // Owner / Admin role gate — hides the Refund + Write Off affordances
  // for front-desk staff. Falls back to true when prop isn't wired so
  // the component stays usable in storybook / dev harnesses.
  canManageRefunds = true,
  // Deposit-flow gate (Feb 2026): the calendar's "Take Deposit"
  // shortcut sends `?mode=deposit` — when true the invoice MUST stay
  // in Open / Partial state (cash-flow tracking) and never surface
  // the Close Order button. The operator can still take more
  // payments up to the balance, but finalising is done later via
  // the regular Take-Payment/Checkout entry point.
  depositMode = false,
}) {
  // Branch-local today — payments stamped on this date keep the X
  // (delete) affordance; everything older swaps to Refund per Fix 1.
  const _todayBranch = _todayInTz(branch?.timezone);
  return (
    <Card className="rounded-lg text-xs" data-testid="payment-section-compact">
      <CardContent className="p-0">
        <div className="px-4 py-3 bg-rose-50 border-b border-rose-100 flex items-center justify-between">
          <span className="uppercase tracking-wider text-xs font-semibold text-rose-900">Payment</span>
        </div>

        {/* Closed lifecycle (voided/refunded/completed) → the entire
            payment-entry area is replaced with a status banner. */}
        {isClosed ? (
          <div className="m-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2" data-testid="invoice-paid-banner">
            <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
            <span className="text-sm text-emerald-900 font-medium">
              {order.status === "voided"
                ? "This invoice is voided — no payment can be taken."
                : order.status === "refunded"
                ? "This invoice was refunded — no payment can be taken."
                : order.status === "merged"
                ? `This invoice was merged into Group Order #${order.merged_into_order_number || ""} — no further changes allowed.`
                : order.status === "closed_no_show"
                ? "Closed as No-Show — Take Payment is disabled. Revert the no-show on the calendar to restore this invoice to Draft."
                : "This invoice is fully paid — no further payment needed."}
            </span>
          </div>
        ) : (due < 0.01) ? (
          /* Fully-paid but still OPEN: waiting on the operator to
             click Close Order. Covers TWO cases:
              1. Regular paid bill (total > 0, amount_paid ≥ total).
              2. Zero-value / free-consultation bill (total ≈ 0) —
                 treated as fully paid immediately with NO Add Payment
                 step. Banner copy adapts to which case we're in.
             The treatment / items / date remain editable until the
             button is pressed.
             DEPOSIT-FLOW EXCEPTION (Feb 2026): when the operator
             entered via the "Take Deposit" shortcut, `depositMode`
             is true and the invoice MUST stay Open even when
             fully paid — the Close Order button is suppressed and
             the banner tells the operator to switch to the regular
             Take-Payment / Checkout flow to finalise. */
          depositMode ? (
            <div className="m-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2" data-testid="deposit-active-banner">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <span className="text-sm text-emerald-900 font-medium">
                Deposit active — this invoice stays open. Reopen via Take Payment/Check-Out to close it.
              </span>
            </div>
          ) : (
          <div className="m-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="invoice-ready-to-close-banner">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
              <span className="text-sm text-emerald-900 font-medium" data-testid="ready-to-close-banner-text">
                {total <= 0.01
                  ? "Free consultation / zero-value bill — fully paid, ready to close."
                  : "Fully paid — order stays open until you close it. You can still edit the treatment or add items."}
              </span>
            </div>
            <Button
              onClick={closeOrder}
              data-testid="close-order-btn"
              className="bg-slate-800 hover:bg-slate-900 text-white shrink-0"
            >Close Order</Button>
          </div>
          )
        ) : (
          <>
        {/* Series slide-down: the client owns pre-paid sessions matching a
            treatment on this bill — one tap pays that line with a session. */}
        {seriesOptions.length > 0 && (
          <div className="mx-4 mt-4 rounded-md border border-sky-300 bg-sky-50/70 overflow-hidden" data-testid="series-payment-panel">
            <button
              className="w-full flex items-center justify-between px-3 py-2 bg-sky-100/80 hover:bg-sky-100"
              onClick={() => setSeriesPanelOpen((v) => !v)}
              data-testid="series-panel-toggle"
            >
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-900">
                <Layers className="w-4 h-4" /> Pay with series · {seriesOptions.length} match{seriesOptions.length === 1 ? "" : "es"}
              </span>
              <ChevronDown className={"w-4 h-4 text-sky-900 transition-transform " + (seriesPanelOpen ? "rotate-180" : "")} />
            </button>
            {seriesPanelOpen && seriesOptions.map((opt, i) => (
              <button
                key={`${opt.client_series_id}-${opt.item_index}`}
                onClick={() => payWithSeries(opt)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-sky-100 border-t border-sky-200/70"
                data-testid={`series-option-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-sky-950 truncate">{opt.series_name}</div>
                  <div className="text-[11px] text-sky-800">
                    pays <span className="font-medium">{opt.item_name}</span> · <b>{opt.quantity_remaining}</b> of {opt.quantity_total} session{opt.quantity_total === 1 ? "" : "s"} left · {clientSeriesValidity(opt).text}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-sky-700">per session</div>
                  <div className="text-sm font-semibold text-sky-950">{formatBranchMoney(branch, opt.per_session_price)}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {activeCategories.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No active payment types configured. Add one under <span className="font-medium">Branch Settings → Payment Types</span> to take payment.
          </div>
        ) : (
          <>
            <Tabs value={paymentTab} onValueChange={setPaymentTab} className="px-3 pt-2" data-testid="payment-tabs-compact">
              <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-full justify-start gap-1 h-auto flex-wrap">
                {activeCategories.map((c) => (
                  <TabsTrigger
                    key={c.value}
                    value={c.value}
                    data-testid={`payment-tab-${c.value}`}
                    className="rounded-t-md rounded-b-none border data-[state=active]:border-rose-400 data-[state=active]:bg-white data-[state=active]:text-rose-800 data-[state=inactive]:bg-rose-100 data-[state=inactive]:text-rose-900 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
                  >{c.label}</TabsTrigger>
                ))}
              </TabsList>

              {activeCategories.map((c) => (
                <TabsContent key={c.value} value={c.value} className="mt-0 pt-2 pb-2 border border-rose-200 border-t-0 rounded-b-md px-3">
                  <div className="grid grid-cols-1 md:grid-cols-[90px_1fr] gap-y-1.5 items-center text-xs">
                    <Label className="text-xs"><span className="text-rose-700 mr-1">*</span> Type:</Label>
                    <Select value={paymentTypeId} onValueChange={setPaymentTypeId}>
                      <SelectTrigger className="max-w-xs h-7 text-xs" data-testid={`payment-type-select-${c.value}`}><SelectValue placeholder="-- Select One --" /></SelectTrigger>
                      <SelectContent>
                        {typesForTab.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {c.value === "online" && (
                      <>
                        <Label className="text-xs"><span className="text-rose-700 mr-1">*</span> Provider:</Label>
                        <Select value={onlineProvider} onValueChange={setOnlineProvider}>
                          <SelectTrigger className="max-w-xs h-7 text-xs" data-testid="online-provider-select"><SelectValue placeholder="Choose provider" /></SelectTrigger>
                          <SelectContent>
                            {onlineProviders.map((p) => (
                              <SelectItem key={p.value} value={p.value} data-testid={`online-provider-option-${p.value}`}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    {isVoucherType && (
                      <>
                        <Label className="text-xs"><span className="text-rose-700 mr-1">*</span> Voucher #:</Label>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Input
                              value={voucherCode}
                              onChange={(e) => { setVoucherCode(e.target.value.toUpperCase()); setVoucherInfo(null); }}
                              onKeyDown={(e) => e.key === "Enter" && searchVoucher()}
                              placeholder="GC-XXXX-XXXX"
                              className="max-w-xs font-mono h-7 text-xs"
                              data-testid="voucher-code-input"
                            />
                            <Button size="sm" variant="outline" onClick={searchVoucher} disabled={voucherChecking} data-testid="voucher-search-btn" className="h-7 px-2 text-[11px]">
                              <SearchIcon className="w-3 h-3 mr-1" /> {voucherChecking ? "Checking…" : "Search"}
                            </Button>
                          </div>
                          {voucherInfo && (voucherInfo.valid ? (
                            <div className="text-[11px] rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-1.5 max-w-md" data-testid="voucher-valid-info">
                              <span className="font-semibold">Valid</span> · Balance <b>{formatBranchMoney(branch, voucherInfo.card.balance)}</b> of {formatBranchMoney(branch, voucherInfo.card.initial_value)} · expires {fmtDate(voucherInfo.card.expires_at)}
                              {Number(voucherInfo.card.balance) < due - 0.01 && (
                                <div className="mt-1 text-amber-800" data-testid="voucher-partial-note">
                                  Covers part of the bill — remaining {formatBranchMoney(branch, due - Number(voucherInfo.card.balance))} must be paid with another method.
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11px] rounded-md border border-rose-200 bg-rose-50 text-rose-900 px-3 py-1.5 max-w-md" data-testid="voucher-invalid-info">
                              {voucherInfo.reason || "Voucher is not valid."}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    <Label className="text-xs"><span className="text-rose-700 mr-1">*</span> Amount:</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{branch?.currency || "IDR"}</span>
                      <Input type="number" min={0} step="1" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} className="max-w-xs h-7 text-xs" data-testid="payment-amount-input" />
                    </div>
                  </div>
                  <div className="flex items-center justify-end mt-2 gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setPaymentAmount(String(due))} data-testid="fill-due-btn" className="h-7 px-2 text-[11px]">Fill balance ({formatBranchMoney(branch, due)})</Button>
                    <Button size="sm" onClick={addPayment} data-testid="add-payment-btn" className="h-7 px-3 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white">Add this Payment</Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
          </>
        )}

        {(order.payments || []).length > 0 && (
          <div className="border-t border-border px-4 py-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recorded payments</p>
            <div className="space-y-1">
              {(order.payments || []).map((p) => {
                const payDate = _dateInTz(p.at, branch?.timezone);
                const isToday = payDate && payDate === _todayBranch;
                const isDeposit = !!p.is_deposit;
                const depStatus = p.deposit_status || (isDeposit ? "pending" : null);
                const depLabel = {
                  pending:     "Deposit · Pending",
                  converted:   "Deposit · Converted",
                  refunded:    "Deposit · Refunded",
                  written_off: "Deposit · Written Off",
                }[depStatus] || "Deposit";
                const depToneCls = {
                  pending:     "bg-amber-50 text-amber-800 border-amber-200",
                  converted:   "bg-emerald-50 text-emerald-800 border-emerald-200",
                  refunded:    "bg-rose-50 text-rose-800 border-rose-200",
                  written_off: "bg-zinc-100 text-zinc-700 border-zinc-300",
                }[depStatus] || "bg-amber-50 text-amber-800 border-amber-200";
                return (
                <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`payment-line-${p.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="rounded-full text-[10px]">{paymentTabsMeta.find((t) => t.value === p.category)?.label || "Other"}</Badge>
                    {p.online_provider && (
                      <Badge variant="outline" className="rounded-full text-[10px] bg-indigo-50 text-indigo-800 border-indigo-200" data-testid={`payment-provider-${p.id}`}>{onlineProviderLabel[p.online_provider] || p.online_provider}</Badge>
                    )}
                    {isDeposit && (
                      <Badge variant="outline" className={`rounded-full text-[10px] ${depToneCls}`} data-testid={`payment-deposit-badge-${p.id}`}>{depLabel}</Badge>
                    )}
                    <span className="font-medium truncate">{p.payment_type_name}{p.gift_card_code ? ` · ${p.gift_card_code}` : ""}</span>
                    {p.client_series_id && p.note && <span className="text-xs text-sky-700 truncate">{p.note}</span>}
                    <span className="text-xs text-muted-foreground truncate">by {p.by_name} · {fmtDateTime(p.at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatBranchMoney(branch, p.amount)}</span>
                    {/* Fix 1 (Iter-f10/f11): same-day payments keep
                        the X delete affordance for a clean undo. Any
                        payment stamped before today (in the branch's
                        own timezone) swaps to a Refund button
                        (Owner/Admin only). Pending deposit payments
                        also expose a Write Off action next to Refund. */}
                    {order.status === "open" && isToday && (
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removePayment(p.id)} data-testid={`remove-payment-${p.id}`}><X className="w-3 h-3" /></Button>
                    )}
                    {order.status === "open" && !isToday && canManageRefunds && onRefundPayment && (depStatus !== "refunded" && depStatus !== "written_off") && (
                      <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px] text-amber-800 border-amber-300 hover:bg-amber-50" onClick={() => onRefundPayment(p)} data-testid={`refund-payment-${p.id}`}>
                        <Undo2 className="w-3 h-3" /> Refund
                      </Button>
                    )}
                    {order.status === "open" && isDeposit && depStatus === "pending" && canManageRefunds && onWriteOffDeposit && (
                      <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px] text-zinc-700 border-zinc-300 hover:bg-zinc-50" onClick={() => onWriteOffDeposit(p)} data-testid={`writeoff-deposit-${p.id}`}>
                        <FileX className="w-3 h-3" /> Write Off
                      </Button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
        {(order.refunds || []).length > 0 && (
          <div className="border-t border-border px-4 py-3">
            <p className="text-xs uppercase tracking-wider text-rose-700 mb-2">Refunds issued</p>
            <div className="space-y-1">
              {(order.refunds || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`refund-line-${r.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="rounded-full text-[10px] bg-rose-100 text-rose-800 border-rose-200">Refunded</Badge>
                    <span className="font-medium capitalize truncate">{(r.payout_type || "").replace(/_/g, " ")}</span>
                    {r.gift_card_code && (
                      <span className="flex items-center gap-1.5 text-xs">
                        <span className="font-mono" data-testid={`refund-gift-code-${r.id}`}>{r.gift_card_code}</span>
                        <GiftCardActions code={r.gift_card_code} />
                      </span>
                    )}
                    {r.note && <span className="text-xs text-muted-foreground truncate">{r.note}</span>}
                    <span className="text-xs text-muted-foreground truncate">by {r.by_name} · {fmtDateTime(r.at)}</span>
                  </div>
                  <span className="font-medium text-rose-700">− {formatBranchMoney(branch, r.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
